import { useForm } from "react-hook-form";
import Swal from "sweetalert2";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";

import IndexBody from "../../components/Cadastro/body/IndexBody";
import IndexFooter from "../../components/Cadastro/footer/IndexFooter";
import Input from "../../components/Input/index";
import Dropdown from "../../components/Dropdown";
import { Validator } from "../../utility/validations";
import Button from "../../components/Button";
import { selectUserToken } from "../../features/user";
import divisaoApi from "../../utility/api/divisaoApi";
import ButtonClasses from "../../components/Button/typing";
import CancelarButton from "../../components/CancelarButton";
import useQuery from "../../utility/useQuery";
import Toggle from "../../components/Toggle";
import { useNavigate } from "react-router-dom";
import { NotAllowed, permissoes, useAthorize } from "../../hooks/useAthorize";

const EdicaoDivisao = () => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm();

  let isAllowed = useAthorize(
    permissoes.divisaoConsulta,
    permissoes.divisaoFull
  );

  const query = useQuery();
  let divisaoId = parseInt(query.get("divisaoId") ?? 0);

  const token = useSelector(selectUserToken);
  const navigate = useNavigate();
  const [paises, setPaises] = useState([]);
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);
  const [divisao, setDivisao] = useState({});

  const fetch = useCallback(
    async (requestFunction, setter, data, token, reset) => {
      var result = await requestFunction(data, token);

      if (result.hasError) {
        Swal.fire({
          title: "Erro!",
          text: "Algo deu errado",
          icon: "error",
        });
        return;
      }

      setter(result.data);
      reset?.(result.data);
    },
    []
  );

  const listarPaises = useCallback(async () => {
    fetch(divisaoApi.listarPaises, setPaises, {}, token);
  }, [fetch, token]);

  const listarEstados = useCallback(
    async (getValues) => {
      let paisId = parseInt(getValues("paisId"));

      if (isNaN(paisId)) return;

      fetch(
        divisaoApi.listarEstados,
        setEstados,
        {
          paisId,
        },
        token
      );
    },
    [fetch, token]
  );

  const listarCidades = useCallback(
    async (getValues) => {
      let paisId = parseInt(getValues("paisId"));
      let estadoId = parseInt(getValues("estadoId"));

      if (isNaN(paisId)) return;
      if (isNaN(estadoId)) return;

      fetch(
        divisaoApi.listarCidades,
        setCidades,
        {
          paisId,
          estadoId,
        },
        token
      );
    },
    [fetch, token]
  );

  useEffect(() => {
    listarPaises();
    listarEstados(getValues);
    listarCidades(getValues);
  }, [getValues, listarCidades, listarEstados, listarPaises]);

  useEffect(() => {
    fetch(divisaoApi.buscarDivisao, setDivisao, { divisaoId }, token, reset);
  }, [divisaoId, fetch, reset, token]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name !== "paisId") return;
      listarEstados(getValues);
      listarCidades(getValues);
    });
    return () => subscription.unsubscribe();
  }, [getValues, listarCidades, listarEstados, watch]);

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name !== "estadoId") return;
      listarCidades(getValues);
    });
    return () => subscription.unsubscribe();
  }, [getValues, listarCidades, watch]);

  const cancelForm = () => {
    Swal.fire({
      text: "Realmente deseja cancelar o cadastro?",
      showDenyButton: true,
      confirmButtonText: "Sim",
      denyButtonText: `Não`,
    }).then(async (result) => {
      if (result.isConfirmed) {
        reset();

        Swal.fire({
          text: "Cancelado com sucesso!",
          icon: "success",
        });
      }
    });
  };

  const onSubmit = async ({
    nome,
    paisId,
    estadoId,
    cidadeId,
    razaoSocial,
    referencia,
    status,
  }) => {
    let data = {
      divisaoId,
      nome,
      paisId,
      estadoId,
      cidadeId,
      razaoSocial,
      referencia,
      status,
    };

    let result = await divisaoApi.atualizarDivisao(data, token);

    if (result.hasError) {
      Swal.fire({
        title: "Erro!",
        text: "Algo deu errado",
        icon: "error",
      });
      return;
    }

    if (result.data.length === 0) {
      reset(data);
      setDivisao(data);

      Swal.fire({
        title: "Sucesso!",
        text: "Cadastrado com sucesso",
        icon: "success",
      });
      return;
    }

    Swal.fire({
      title: "Erro!",
      text: result.data,
      icon: "error",
    });
  };

  if (!isAllowed) return <NotAllowed />;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="w-full min-h-screen mb-[60px]"
    >
      <IndexBody
        width={"w-[65%]"}
        height={"min-h-[350px]"}
        title="Editar Divisão"
      >
        <div className="flex h-full">
          <div className="w-full px-16 py-14 grid grid-cols-2 grid-flow-col gap-x-28 gap-y-2 grid-rows-[repeat(4,_auto)]">
            <Input
              label="Divisão"
              name="nome"
              type="text"
              placeholder="Insira a divisão"
              register={register}
              validate={(text) =>
                new Validator.Builder().required().build().validate(text)
              }
              errors={errors}
            />

            <Input
              label="Razão Social"
              name="razaoSocial"
              type="text"
              placeholder="Insira a razão social"
              register={register}
              validate={(text) =>
                new Validator.Builder().required().build().validate(text)
              }
              errors={errors}
            />

            <Input
              label="Referência"
              name="referencia"
              type="text"
              placeholder="Insira a referência"
              register={register}
              validate={(text) =>
                new Validator.Builder().required().build().validate(text)
              }
              errors={errors}
            />

            <div>
              <span className="text-[#8A92A6] text-[12px]">Status</span>
              <Toggle
                register={register}
                setValue={setValue}
                watch={watch}
                name="status"
                defaultValue={divisao.status}
                label="Status"
              />
            </div>

            <Dropdown
              name="paisId"
              defaultText="Selecione o país"
              register={register}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
              options={paises}
              width={"w-full"}
              label="País"
              errors={errors}
              defaultValue={divisao.paisId}
              required
            />

            <Dropdown
              name="estadoId"
              defaultText="Selecione o estado"
              register={register}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
              options={estados}
              width={"w-full"}
              label="Estado"
              errors={errors}
              defaultValue={divisao.estadoId}
              required
              readonly={!Boolean(getValues("paisId"))}
            />

            <Dropdown
              name="cidadeId"
              register={register}
              setValue={setValue}
              watch={watch}
              getValues={getValues}
              defaultText="Selecione a cidade"
              options={cidades}
              width={"w-full"}
              label="Cidade"
              errors={errors}
              defaultValue={divisao.cidadeId}
              required
              readonly={
                !(
                  Boolean(getValues("paisId")) && Boolean(getValues("estadoId"))
                )
              }
            />
          </div>
        </div>
      </IndexBody>
      <IndexFooter
        width={"w-[65%]"}
        height={"h-[100px]"}
        className="flex items-center justify-between px-11"
      >
        <CancelarButton
          wide
          voltar={() => navigate("/divisao/listagem")}
          watch={watch}
          reset={cancelForm}
          getValues={getValues}
        />
        <Button
          wide
          text="Salvar"
          type="submit"
          colorClass={ButtonClasses.DarkGreen}
        />
      </IndexFooter>
    </form>
  );
};
export default EdicaoDivisao;
